/**
 * The voice chat engine (VoiceController, see types.ts): a WebRTC mesh, one PeerLink per
 * other room member, plus the microphone, volumes and the speaking marks. Browser APIs are
 * behind VoiceMedia (media.ts), so all of this runs in Node tests with fakes.
 *
 * - enable() (inside the tap): unlocks audio playback and calls getUserMedia before its
 *   first await; voice is on from then on and the mic switch starts unmuted. The offerer
 *   links connect once the microphone answered (after the permission the browser reveals
 *   the real local addresses, which helps devices on the same Wi-Fi find each other) or
 *   after CONNECT_WAIT_MS, whichever comes first.
 * - Mute = the microphone track is disabled: it keeps running and sends silence, so muting
 *   is instant, never renegotiates and the other side's jitter buffer stays warm (no
 *   clipped first word after unmuting). The microphone stays open while voice is on.
 * - Speaking marks: levels of the local mic and of every remote voice, polled 10 times a
 *   second (not while the page is hidden), with hysteresis (speaking.ts).
 * - Out of every room for RELEASE_MS (logged out, the session moved to another device):
 *   the microphone is released and voice is off again until the next enable().
 */
import type { PlayerId, RtcSignal } from '@cube-rush/shared';
import { batch, type ReadonlySignal, type Signal, signal } from '@preact/signals';
import type { MicCapture, MicPermissionState, MicResult, VoiceMedia } from './media';
import { PeerLink, type PeerLinkContext } from './peerLink';
import { SignalPacer } from './signalQueue';
import { SpeakingDetector } from './speaking';
import type { MicPermission, VoiceController, VoiceControllerDeps, VoicePeer } from './types';

export const ENGINE_TIMING = {
  /** Speaking marks are updated this often, ms. */
  pollMs: 100,
  /** Offerer links wait at most this long for the microphone before connecting, ms. */
  connectWaitMs: 4000,
  /** An empty member list for this long releases the microphone, ms. */
  releaseMs: 8000,
  /** After a lost microphone (unplugged, a phone call) it is reopened after this delay, ms. */
  micRetryMs: 1000,
  /** Device changes are handled once they settle, ms. */
  deviceChangeDebounceMs: 400,
};

const NOBODY: ReadonlySet<PlayerId> = new Set();

export class VoiceEngine implements VoiceController {
  private readonly enabledSignal: Signal<boolean> = signal(false);
  private readonly mutedSignal: Signal<boolean> = signal(true);
  private readonly micAvailableSignal: Signal<boolean> = signal(false);
  private readonly micPermissionSignal: Signal<MicPermission>;
  private readonly micRequestingSignal: Signal<boolean> = signal(false);
  private readonly speakingSignal: Signal<ReadonlySet<PlayerId>> = signal(NOBODY);
  private readonly peersSignal: Signal<readonly VoicePeer[]> = signal([]);

  readonly enabled: ReadonlySignal<boolean> = this.enabledSignal;
  readonly muted: ReadonlySignal<boolean> = this.mutedSignal;
  readonly micAvailable: ReadonlySignal<boolean> = this.micAvailableSignal;
  readonly micPermission: ReadonlySignal<MicPermission>;
  readonly micRequesting: ReadonlySignal<boolean> = this.micRequestingSignal;
  readonly speaking: ReadonlySignal<ReadonlySet<PlayerId>> = this.speakingSignal;
  readonly peers: ReadonlySignal<readonly VoicePeer[]> = this.peersSignal;

  private readonly links = new Map<PlayerId, PeerLink>();
  /** Per-player volumes outlive the links (a friend who reconnects keeps their volume). */
  private readonly volumes = new Map<PlayerId, number>();
  private readonly pacer: SignalPacer;
  private readonly localSpeaking = new SpeakingDetector();
  private readonly cleanups: Array<() => void> = [];
  private myId: PlayerId | null = null;
  private order: PlayerId[] = [];
  private mic: MicCapture | null = null;
  private micEndedUnsubscribe: (() => void) | null = null;
  private micRequest: Promise<void> | null = null;
  /** The microphone worked at some point since voice was turned on. */
  private hadMic = false;
  private active = false;
  private othersTalking = false;
  private disposed = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private micRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private deviceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly linkContext: PeerLinkContext;

  constructor(
    deps: VoiceControllerDeps,
    private readonly media: VoiceMedia,
  ) {
    this.micPermissionSignal = signal<MicPermission>(
      media.micSupported ? 'unknown' : 'unsupported',
    );
    this.micPermission = this.micPermissionSignal;
    this.pacer = new SignalPacer((to, data) => deps.sendSignal(to, data));
    this.linkContext = {
      media,
      iceServers: () => deps.iceServers.peek(),
      send: (to, data, priority, stale) => this.pacer.enqueue(to, data, priority, stale),
      mic: () => this.mic,
      active: () => this.active,
      changed: () => this.publishPeers(),
    };
    if (media.micSupported) {
      this.cleanups.push(media.watchMicPermission((state) => this.onPermission(state)));
    }
    this.cleanups.push(
      media.onDeviceChange(() => this.onDeviceChange()),
      media.onVisible(() => this.onVisible()),
    );
  }

  // ---- VoiceController ----

  enable(): Promise<void> {
    if (this.disposed) return Promise.resolve();
    // Both synchronously, inside the user gesture (iOS allows them only there).
    this.media.unlockPlayback();
    const request = this.openMic();
    this.media.setPlaybackEnabled(true);
    if (!this.enabledSignal.peek()) {
      batch(() => {
        this.enabledSignal.value = true;
        this.mutedSignal.value = false;
      });
      this.startPolling();
    }
    if (!this.active) {
      this.active = true;
      void settleWithin(request, ENGINE_TIMING.connectWaitMs).then(() => {
        if (this.active && !this.disposed) for (const link of this.links.values()) link.activate();
      });
    }
    return request ?? Promise.resolve();
  }

  setMuted(muted: boolean): void {
    if (this.disposed) return;
    this.mutedSignal.value = muted;
    this.mic?.setEnabled(!muted);
    if (muted) {
      this.localSpeaking.reset();
      this.publishSpeaking();
    }
  }

  toggleMute(): void {
    this.setMuted(!this.mutedSignal.peek());
  }

  setPeerVolume(id: PlayerId, volume: number): void {
    const clamped = Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : 1;
    this.volumes.set(id, clamped);
    this.links.get(id)?.setVolume(clamped);
    this.publishPeers();
  }

  setMasterVolume(volume: number): void {
    this.media.setMasterVolume(Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : 1);
  }

  handleSignal(from: PlayerId, data: RtcSignal): void {
    if (this.disposed) return;
    // Only members of the room (the latest list) may connect; a link exists for each.
    void this.links.get(from)?.handleSignal(data);
  }

  syncPeers(memberIds: readonly PlayerId[], myId: PlayerId): void {
    if (this.disposed) return;
    if (this.myId !== null && this.myId !== myId) this.closeLinks();
    this.myId = myId;
    const wanted: PlayerId[] = [];
    for (const id of memberIds) if (id !== myId && !wanted.includes(id)) wanted.push(id);
    for (const [id, link] of this.links) {
      if (wanted.includes(id)) continue;
      link.close();
      this.links.delete(id);
      this.pacer.drop(id);
    }
    for (const id of wanted) {
      if (this.links.has(id)) continue;
      const link = new PeerLink(id, myId, this.linkContext);
      link.setVolume(this.volumes.get(id) ?? 1);
      this.links.set(id, link);
      link.start();
    }
    this.order = wanted;
    this.publishPeers();
    this.publishSpeaking();
    if (memberIds.length === 0) this.scheduleRelease();
    else this.cancelRelease();
  }

  dispose(): void {
    if (this.disposed) return;
    this.release();
    this.disposed = true;
    this.closeLinks();
    this.pacer.dispose();
    for (const cleanup of this.cleanups.splice(0)) cleanup();
    this.clearTimer('deviceTimer');
    this.media.dispose();
    batch(() => {
      this.peersSignal.value = [];
      this.speakingSignal.value = NOBODY;
    });
  }

  // ---- microphone ----

  /** Starts getUserMedia (synchronously) unless a mic is open or being opened. */
  private openMic(): Promise<void> | null {
    if (this.mic?.live || this.micRequest) return this.micRequest;
    if (!this.media.micSupported) {
      this.micPermissionSignal.value = 'unsupported';
      return null;
    }
    this.clearTimer('micRetryTimer');
    this.micRequestingSignal.value = true;
    const request = this.media
      .requestMic()
      .then(
        (result) => this.onMicResult(result),
        () => this.onMicResult({ ok: false, reason: 'error' }),
      )
      .finally(() => {
        this.micRequest = null;
        this.micRequestingSignal.value = false;
      });
    this.micRequest = request;
    return request;
  }

  private onMicResult(result: MicResult): void {
    if (this.disposed || !this.enabledSignal.peek()) {
      if (result.ok) result.mic.stop();
      return;
    }
    if (result.ok) {
      this.attachMic(result.mic);
      return;
    }
    batch(() => {
      this.micAvailableSignal.value = false;
      // A refusal after the mic worked in this session is not the player's "no": iOS refuses
      // capture to a page in the background or right after a call. Tried again on screen.
      if (result.reason === 'denied' && !this.hadMic) {
        this.micPermissionSignal.value = 'denied';
      } else if (result.reason === 'unsupported') {
        this.micPermissionSignal.value = 'unsupported';
      }
    });
  }

  private attachMic(mic: MicCapture): void {
    this.detachMic();
    this.mic = mic;
    this.hadMic = true;
    mic.setEnabled(!this.mutedSignal.peek());
    this.micEndedUnsubscribe = mic.onEnded(() => this.onMicEnded(mic));
    batch(() => {
      this.micPermissionSignal.value = 'granted';
      this.micAvailableSignal.value = true;
    });
    for (const link of this.links.values()) link.setMic(mic);
  }

  private detachMic(): void {
    this.micEndedUnsubscribe?.();
    this.micEndedUnsubscribe = null;
    const mic = this.mic;
    this.mic = null;
    if (!mic) return;
    mic.stop();
    for (const link of this.links.values()) link.setMic(null);
    this.micAvailableSignal.value = false;
    this.localSpeaking.reset();
  }

  /** The device went away (unplugged, permission revoked, a phone call took it). */
  private onMicEnded(mic: MicCapture): void {
    if (mic !== this.mic) return;
    this.detachMic();
    this.publishSpeaking();
    this.scheduleMicRetry();
  }

  private scheduleMicRetry(): void {
    if (this.micRetryTimer !== null || !this.enabledSignal.peek()) return;
    this.micRetryTimer = setTimeout(() => {
      this.micRetryTimer = null;
      // A page in the background gets no microphone (iOS): back on screen, onVisible asks.
      if (this.media.isHidden()) return;
      if (this.enabledSignal.peek() && !this.mic && this.micPermissionSignal.peek() !== 'denied') {
        void this.openMic();
      }
    }, ENGINE_TIMING.micRetryMs);
  }

  /** Back on screen with voice on but no microphone (lost in the background): ask again. */
  private onVisible(): void {
    if (this.disposed || !this.enabledSignal.peek() || this.mic?.live || this.micRequest) return;
    const permission = this.micPermissionSignal.peek();
    if (permission !== 'denied' && permission !== 'unsupported') void this.openMic();
  }

  private onPermission(state: MicPermissionState): void {
    if (this.disposed) return;
    if (state === 'granted') {
      this.micPermissionSignal.value = 'granted';
      // Allowed in the settings after a refusal: open the mic without another tap.
      if (this.enabledSignal.peek() && !this.mic && !this.micRequest) void this.openMic();
    } else if (state === 'denied') {
      this.micPermissionSignal.value = 'denied';
    } else if (state === 'prompt' && !this.mic) {
      this.micPermissionSignal.value = 'unknown';
    }
  }

  private onDeviceChange(): void {
    this.clearTimer('deviceTimer');
    this.deviceTimer = setTimeout(() => {
      this.deviceTimer = null;
      // A headset with a microphone appeared, or ours went away: try to (re)open it.
      if (!this.enabledSignal.peek() || this.micRequest) return;
      if (this.micPermissionSignal.peek() === 'denied') return;
      if (!this.mic?.live) void this.openMic();
    }, ENGINE_TIMING.deviceChangeDebounceMs);
  }

  // ---- links ----

  private closeLinks(): void {
    for (const [id, link] of this.links) {
      link.close();
      this.pacer.drop(id);
    }
    this.links.clear();
    this.order = [];
  }

  private publishPeers(): void {
    if (this.disposed) return;
    const next: VoicePeer[] = [];
    for (const id of this.order) {
      const link = this.links.get(id);
      if (link) next.push({ id, state: link.state, volume: this.volumes.get(id) ?? 1 });
    }
    const current = this.peersSignal.peek();
    const same =
      current.length === next.length &&
      current.every((peer, i) => {
        const other = next[i];
        return (
          other !== undefined &&
          peer.id === other.id &&
          peer.state === other.state &&
          peer.volume === other.volume
        );
      });
    if (!same) this.peersSignal.value = next;
  }

  // ---- speaking marks ----

  private startPolling(): void {
    if (this.pollTimer === null)
      this.pollTimer = setInterval(() => this.poll(), ENGINE_TIMING.pollMs);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  private poll(): void {
    if (this.disposed || this.media.isHidden()) return;
    const mic = this.mic;
    this.localSpeaking.update(
      mic && !this.mutedSignal.peek() ? mic.level() : Number.NEGATIVE_INFINITY,
    );
    for (const link of this.links.values()) link.speaking.update(link.level());
    this.publishSpeaking();
  }

  private publishSpeaking(): void {
    const next = new Set<PlayerId>();
    if (this.myId !== null && this.mic && !this.mutedSignal.peek() && this.localSpeaking.value) {
      next.add(this.myId);
    }
    let others = false;
    if (this.enabledSignal.peek()) {
      for (const [id, link] of this.links) {
        if (link.hasAudio && link.speaking.value) {
          next.add(id);
          others = true;
        }
      }
    }
    const current = this.speakingSignal.peek();
    if (current.size !== next.size || [...next].some((id) => !current.has(id))) {
      this.speakingSignal.value = next.size === 0 ? NOBODY : next;
    }
    if (others !== this.othersTalking) {
      this.othersTalking = others;
      this.media.setRemoteVoiceActive(others);
    }
  }

  // ---- leaving ----

  private scheduleRelease(): void {
    if (this.releaseTimer !== null || !this.enabledSignal.peek()) return;
    this.releaseTimer = setTimeout(() => {
      this.releaseTimer = null;
      this.release();
    }, ENGINE_TIMING.releaseMs);
  }

  private cancelRelease(): void {
    this.clearTimer('releaseTimer');
  }

  /** Voice off: the microphone is closed, playback muted, back to "turn voice on". */
  private release(): void {
    this.cancelRelease();
    this.clearTimer('micRetryTimer');
    this.stopPolling();
    this.active = false;
    this.detachMic();
    this.hadMic = false;
    this.localSpeaking.reset();
    if (this.othersTalking) {
      this.othersTalking = false;
      this.media.setRemoteVoiceActive(false);
    }
    this.media.setPlaybackEnabled(false);
    batch(() => {
      this.enabledSignal.value = false;
      this.mutedSignal.value = true;
      this.micAvailableSignal.value = false;
      this.speakingSignal.value = NOBODY;
    });
  }

  private clearTimer(name: 'releaseTimer' | 'micRetryTimer' | 'deviceTimer'): void {
    const timer = this[name];
    if (timer !== null) clearTimeout(timer);
    this[name] = null;
  }
}

/** Resolves when `promise` settles or after `ms`, whichever comes first. */
function settleWithin(promise: Promise<void> | null, ms: number): Promise<void> {
  if (!promise) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    promise.then(done, done);
  });
}
